import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {PharmacyLocationEnum} from '../PharmacyLocationEnum';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {GetStates} from '../../../states/profile/profile.actions';
import {Select, Store} from '@ngxs/store';
import {Router} from '@angular/router';
import {FormattedDatePipe} from '../../../pipes/formatted-date/formatted-date.pipe';
import {Observable} from 'rxjs/internal/Observable';
import {ProfileState} from '../../../states/profile/profile.state';
import {IState} from '../../../interfaces/state.interface';
import {PharmacyState} from '../../../states/pharmacy/pharmacy.state';
import {GetPharmacyById} from '../../../states/pharmacy/pharmacy.actions';
import {take, withLatestFrom} from 'rxjs/operators';
import { highlightRequiredControlsWithErrors } from 'src/app/shared/helpers/helpers';

export enum NpiEnumerationTypeEnum {
  Individual = 'NPI-1',
  Organization = 'NPI-2'
}
@Component({
  selector: 'app-pharmacy-form',
  templateUrl: './pharmacy-form.component.html',
  styleUrls: ['./pharmacy-form.component.scss'],
})
export class PharmacyFormComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() pharmacyId: string; // edit
  @Input() location: PharmacyLocationEnum;

  @Output() save: EventEmitter<any> = new EventEmitter();
  @Output() cancel: EventEmitter<void> = new EventEmitter();

  today = new Date(new Date().setHours(0, 0, 0, 0));

  PharmacyLocationEnum = PharmacyLocationEnum;
  pharmacyDetailsForm: UntypedFormGroup;

  @ViewChild('pharmacyDetailsInput') pharmacyDetailsInput;
  @ViewChild('phoneNumberInput') phoneNumberInput;
  @ViewChild('streetAddressInput') streetAddressInput;
  @ViewChild('addressExtensionInput') addressExtensionInput;
  @ViewChild('cityInput') cityInput;
  @ViewChild('stateDropdown') stateDropdown;
  @ViewChild('zipInput') zipInput;
  @ViewChild('licenseNumberInput') licenseNumberInput;
  @ViewChild('licenseDateExpDatepicker') licenseDateExpDatepicker;
  @ViewChild('deaNumberInput') deaNumberInput;
  @ViewChild('deaDateExpDatepicker') deaDateExpDatepicker;

  @Select(ProfileState.states) states$: Observable<string[]>;
  @Select(ProfileState.loading) loading$: Observable<IState[]>;
  @Select(PharmacyState.pharmacyForm) pharmacyForm$: Observable<any>;
  @Select(PharmacyState.pharmacyData) pharmacyData$: Observable<any>;
  @Select(PharmacyState.loading) pharmacyLoading$: Observable<IState[]>;

  constructor(
    private fb: UntypedFormBuilder,
    private store: Store,
    private router: Router,
    private formattedDatePipe: FormattedDatePipe,
  ) {
    this.pharmacyDetailsForm = this.fb.group({
      pharmacyName: ['', [Validators.required]],
      phoneNumber: ['', [Validators.required]],
      streetAddress: ['', [Validators.required]],
      addressExtension: ['', []],
      city: ['', [Validators.required]],
      state: ['', [Validators.required]],
      zipCode: ['', [Validators.required]],
      licenseNumber: ['', [Validators.required]],
      licenseExpiration: ['', [Validators.required]],
      deaNumber: ['', [Validators.required]],
      deaExpiration: ['', [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.store.dispatch(new GetStates());

    if (this.pharmacyId) {
      // TODO: create new dialog instance instead
      this.store.dispatch(new GetPharmacyById(this.pharmacyId))
        .pipe(take(1), withLatestFrom(this.pharmacyForm$))
        .subscribe(([_, value]) => {
          if (value && this.pharmacyDetailsForm) {
            this.parsePharmacyData(value);
          } else {
            this.pharmacyData$
              .pipe(take(1))
              .subscribe((data: any) => {
                if (data && !data.empty) {
                  this.parsePharmacyData(data);
                }
              });
          }
        });
    } else {
      this.pharmacyData$
        .pipe(take(1))
        .subscribe((data: any) => {
          if (data && !data.empty) {
            this.parsePharmacyData(data);
          }
        });
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.pharmacyDetailsInput.appInput.nativeElement.focus());
  }

  ngOnDestroy(): void {
    // TODO: create new dialog instance instead
    this.pharmacyDetailsForm.reset();
  }

  parsePharmacyData(data: any): void {
    if (this.pharmacyId) {
      // GetPharmacyById
      const pharmacyData = Object.assign({}, data);
      pharmacyData.licenseExpiration = this.formattedDatePipe.transform(pharmacyData.licenseExpiration, 'MM/dd/yyyy');
      pharmacyData.deaExpiration = this.formattedDatePipe.transform(pharmacyData.deaExpiration, 'MM/dd/yyyy');
      this.pharmacyDetailsForm.patchValue(pharmacyData);
    } else if (data.addresses) {
      // GetPharmacyByNPI
      this.presetPharmacyForm(data);
      setTimeout(() => this.pharmacyDetailsInput.appInput.nativeElement.focus());
    }
  }

  getPharmacyNameFromNpiResult(data: any): string {
    if (!data) {
      return '';
    }

    let pharmacyName = data.basic?.name;

    if (!pharmacyName && data.other_names?.length) {
      pharmacyName = data.other_names?.find((p: any) => p.type === 'Doing Business As')?.organization_name;
    }

    switch (data.enumeration_type as NpiEnumerationTypeEnum) {
      case NpiEnumerationTypeEnum.Organization:
        if (data.basic.name && data.basic.name.length) {
          return data.basic.name;
        }
        return data.basic.organization_name;
      case NpiEnumerationTypeEnum.Individual:
        // individual name is LAST FIRST, so parse this out manually.
        const names = [
          data.basic.name_prefix,
          data.basic.first_name,
          data.basic.middle_name,
          data.basic.last_name,
          data.basic.name_suffix,
          data.basic.name_credential
        ].filter(n => n?.length);
        return names.join(' ');
      default:
        return data.other_names?.find(x => x.type === 'Doing Business As')?.organization_name;
    }
  }

  presetPharmacyForm(data: any): void {
    const address = data.addresses && data.addresses[0];
    const pharmacyName = this.getPharmacyNameFromNpiResult(data);
    const taxonomy = data.taxonomies && data.taxonomies.filter(x => x.license !== '')[0];

    const pharmacyFormObj = {
      pharmacyName: pharmacyName || null,
      phoneNumber: address && address.telephone_number || null,
      streetAddress: address && address.address_1 || null,
      addressExtension: address && address.address_2 || null,
      city: address && address.city || null,
      state: address && address.state || null,
      zipCode: address && address.postal_code || null,
      licenseNumber: taxonomy && taxonomy.license || null,
    };

    this.pharmacyDetailsForm.patchValue(pharmacyFormObj);
  }

  closeForm(): void {
    this.cancel.emit();
  }

  continue(): void {
    if (this.pharmacyDetailsForm.invalid) {
      highlightRequiredControlsWithErrors(this.pharmacyDetailsForm);
    } else {
      const npiNumber = this.store.selectSnapshot(PharmacyState.pharmacyNPI);
      const formValue = this.pharmacyDetailsForm.getRawValue();

      if (npiNumber) {
        formValue.npiNumber = npiNumber;
      }
      if (this.pharmacyId) {
        formValue.id = this.pharmacyId;
      }

      this.save.emit(formValue);
    }
  }

  focusInput(inputName: string): void {
    setTimeout(() => this[inputName] && this[inputName]?.appInput?.nativeElement?.focus());
  }

  focusDatepicker(datepickerName: string): void {
    if (this[datepickerName]) {
      this[datepickerName].datepickerInstance?.open();
    }
  }

  focusDropdown(dropdownName: string): void {
    // if (this[dropdownName]) {
    //   this[dropdownName].showDropdown = true;
    // }
  }
}
